import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useHistory } from 'react-router-dom';

import { openStudentHubWithSso } from '../../../util/openStudentHubWithSso';
import { useIsHubAuthenticated } from '../../app-shell/useIsHubAuthenticated';
import { userSignout } from '../../legacy/actions';
import api from '../../legacy/api';
import { useSelectEnrolmentModal } from '../../legacy/components';
import { PATH_HUB_ADD_ONE_OFF_SESSION } from '../../legacy/pagePath';
import { getActiveEnrolments } from '../../legacy/selectors';
import type { Enrolment, HubEnrolment } from '../../legacy/types';
import { getNavItems } from './navItems';

// Resolves the dynamic state behind the global sidebar nav — account type,
// subscription, bookable enrolments and the actions wired to them. Mirrors the
// behaviour that previously lived in HubHeader so the sidebar can fully replace
// the top nav.
//
// Also used on the public session report page, which has no hub session. In
// that case the account/subscription queries are skipped (there's nothing to
// fetch and no point 401'ing) and the nav falls back to the full "Account"
// item set — see PATH_SESSION_REPORT's usage — so a shared-link viewer sees
// real nav links; clicking one is caught by ProtectedRoute's own login redirect.
export const useAppSidebar = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const isHubAuthenticated = useIsHubAuthenticated();

  const { data: details } = api.account.details.useQuery(undefined, { enabled: isHubAuthenticated });
  const activeEnrolments = useSelector(getActiveEnrolments) as HubEnrolment[];

  const { open, openModal, closeModal, SelectEnrolmentModal } = useSelectEnrolmentModal();

  const onBookSession = useCallback(() => {
    if (activeEnrolments.length === 1) {
      history.push(generatePath(PATH_HUB_ADD_ONE_OFF_SESSION, { enrolmentId: activeEnrolments[0].enrolmentId }));
    } else {
      openModal();
    }
  }, [activeEnrolments, history, openModal]);

  const onStudentHub = useCallback(() => {
    void openStudentHubWithSso({ target: '_blank' });
  }, []);

  const signOut = useCallback(() => {
    dispatch(userSignout());
  }, [dispatch]);

  const { items, bookSessionItem } = useMemo(
    () =>
      getNavItems({
        userType: isHubAuthenticated ? (details?.userType ?? null) : 'Account',
        showBookSession:
          activeEnrolments.length > 0 && activeEnrolments.some((enrolment) => enrolment.type === 'Course'),
        onBookSession,
        onStudentHub,
      }),
    [isHubAuthenticated, details?.userType, activeEnrolments, onBookSession, onStudentHub]
  );

  const enrolmentLinkTo = useCallback(
    (enrolment: Enrolment) => generatePath(PATH_HUB_ADD_ONE_OFF_SESSION, { enrolmentId: enrolment.enrolmentId }),
    []
  );

  return {
    items,
    bookSessionItem,
    signOut,
    enrolmentModal: { open, closeModal, SelectEnrolmentModal, enrolmentLinkTo },
  };
};
